// Master Apps · Critérios da auditoria · drawer informativo sobre a auditoria.
// A descrição desta tela reflete EXATAMENTE a regra implementada em
// @/lib/compliance: 8 critérios de 1 ponto cada, com dispensas por tipo e
// visibilidade, e três faixas de classificação. Nada de pesos, notas de 90
// pontos ou coleta automática que o sistema não faz.
import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { IconCheck, IconLock, IconShield, IconX } from '@/components/icons';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import type { App } from '@/lib/catalog-config';
import type { ComplianceKey, ComplianceLevel } from '@/lib/compliance';
import {
  COMPLIANCE_EXEMPTIONS,
  COMPLIANCE_LABELS,
  COMPLIANCE_LEVEL_LABELS,
  COMPLIANCE_ORDER,
  COMPLIANCE_PARTIAL_FLOOR,
  COMPLIANCE_TOTAL,
  complianceLevelColor,
  complianceOf,
} from '@/lib/compliance';
import type { ScreenProps } from '@/lib/theme';
import { fontMono, fontSans, makePalette, numeroTabular, rotuloCaixaAlta } from '@/lib/theme';
import { ScreenAudit } from './audit-screen';

/**
 * O que a tela explica de cada critério. O que já existe em @/lib/compliance
 * NÃO se repete aqui: a chave é o `ComplianceKey`, o rótulo vem de
 * `COMPLIANCE_LABELS`, a ordem de `COMPLIANCE_ORDER` e o "sempre exigido / com
 * dispensa" sai de `COMPLIANCE_EXEMPTIONS` (dispensa cadastrada = condicional).
 */
interface AuditCriterion {
  readonly description: string;
  /** O que a auditoria espera encontrar (conferência humana). */
  readonly expects: readonly string[];
  readonly evidence: string;
  readonly fail: string;
}

const AUDIT_CRITERIA: Record<ComplianceKey, AuditCriterion> = {
  tech: {
    description:
      'Arquitetura da aplicação, dependências, instruções de deploy e variáveis de ambiente. Permite à TI operar a aplicação sem depender do conhecimento tácito do desenvolvedor.',
    expects: [
      'Link cadastrado no Master Apps',
      'Conteúdo acessível para a equipe de TI',
      'Contém pelo menos: arquitetura, deploy e dependências',
    ],
    evidence: 'URL do Confluence · portal de documentação · repositório',
    fail: 'Aplicações sem documentação técnica são apontadas como risco operacional alto na ISO 27001.',
  },
  biz: {
    description:
      'Documento que descreve o "porquê" da aplicação: processos cobertos, papéis envolvidos, exceções e fluxos de aprovação.',
    expects: [
      'Link cadastrado no Master Apps',
      'Validado pela área de negócio, com data e responsável',
      'Atualizado junto com a última entrega relevante',
    ],
    evidence: 'Confluence da área · ata de validação · processo BPMN',
    fail: 'Risco de divergência entre o que o sistema faz e o que o processo exige (achado de auditoria recorrente).',
  },
  api: {
    description:
      'Para aplicações que expõem APIs (REST, GraphQL, eventos): especificação navegável dos endpoints, schemas e exemplos.',
    expects: [
      'Especificação navegável (Swagger / OpenAPI) cadastrada',
      'Gerada a partir do código, não escrita à mão',
      'Versão alinhada com a que está em produção',
    ],
    evidence: 'URL do Swagger UI · arquivo .yaml/.json no repositório',
    fail: 'Sem doc de API, integrações vão depender de conhecimento não documentado, o que não atende à ISO 27001 A.14.',
  },
  req: {
    description:
      'Documento original de quando a aplicação foi solicitada: entrevistas, escopo, critérios de aceite, mockups.',
    expects: [
      'Link cadastrado (qualquer formato: docx, pdf, SharePoint)',
      'Escopo e critérios de aceite acordados com o solicitante',
    ],
    evidence: 'SharePoint da TI · pasta do projeto',
    fail: 'Sem o levantamento não há como provar o que foi acordado na origem. Importante para apps com mais de 2 anos.',
  },
  repo: {
    description:
      'Onde está o código-fonte versionado. Garante que a Masterboi mantém propriedade intelectual e pode auditar mudanças.',
    expects: [
      'Repositório Git acessível',
      'Proteção de branch ativa na branch principal',
      'Histórico de commits preservado',
    ],
    evidence: 'URL do repositório · captura das regras de proteção',
    fail: 'Aplicações sem repo dependem do desenvolvedor: risco crítico em caso de saída.',
  },
  owner: {
    description:
      'Pessoa nomeada da Masterboi que responde pela aplicação. Não pode ser fornecedor externo.',
    expects: [
      'Responsável cadastrado no campo "responsável"',
      'Time / área de origem definidos',
      'Substituto combinado em caso de ausência',
    ],
    evidence: 'Campo "responsável" preenchido no cadastro da aplicação',
    fail: 'Sem responsável, qualquer incidente fica sem dono. Achado severo na ISO 27001 A.6.',
  },
  proj: {
    description:
      'Identificador do projeto na carteira da TI · liga a aplicação ao planejamento estratégico e ao orçamento.',
    expects: ['Número do projeto preenchido no cadastro', 'Projeto existe na carteira do PMO'],
    evidence: 'Campo "projeto" · referência cruzada com o PMO',
    fail: 'Aplicações sem projeto associado não aparecem na carteira oficial · "shadow IT".',
  },
  tick: {
    description:
      'Chamado original que originou a aplicação · garante rastreabilidade da demanda até a entrega.',
    expects: [
      'Número do chamado de origem preenchido',
      'Chamado localizável no sistema de atendimento',
      'Chamado encerrado com a entrega',
    ],
    evidence: 'Campo "chamado" · link para o ticket',
    fail: 'Sem chamado, não há trilha de "por que esta aplicação existe".',
  },
};

// ── Geometria responsiva do drawer ───────────────────────────────────────────

/**
 * Largura do painel por faixa. Os 720px fixos eram a origem da tela mais quebrada
 * do app: em 375px o drawer começava em −345px e o conteúdo INTEIRO era medido
 * fora da viewport (368 estouros). No celular ele cobre a tela inteira; no tablet
 * continua com os 720px onde eles caibam (em 768px é exatamente o de hoje) e
 * encolhe junto da janela abaixo disso, porque a faixa começa em 641px.
 */
const DRAWER_WIDE: CSSProperties = { width: 720 };
const DRAWER_TABLET: CSSProperties = { width: '100%', maxWidth: 720 };
const DRAWER_NARROW: CSSProperties = { width: '100%' };

/**
 * Geometria do CARTÃO de critério por faixa. Um objeto por faixa em vez de um
 * ternário por propriedade: são cinco decisões no mesmo componente, e o padrão
 * já usado no acervo de documentos (`DOC_WIDE`/`DOC_NARROW`) existe justamente
 * para não estourar o teto de complexidade cognitiva.
 */
interface CriterionLayout {
  padding: number;
  gap: number;
  /** Título e selo de escopo lado a lado (largo) ou embrulhando (celular). */
  titleWrap: CSSProperties['flexWrap'];
  /** "Evidência aceita" e "Impacto se ausente": 2 colunas ou empilhados. */
  fields: string;
}

const CRITERION_WIDE: CriterionLayout = {
  padding: 18,
  gap: 14,
  titleWrap: 'nowrap',
  fields: '1fr 1fr',
};

/**
 * No celular sobram 317px dentro do cartão: o título de 14,5px pede 150px e o
 * selo "sempre exigido" outros 85px, então os dois só cabem embrulhando. As duas
 * colunas do rodapé viram uma — "URL do Confluence · portal de documentação" em
 * 125px de coluna quebra palavra por palavra.
 */
const CRITERION_NARROW: CriterionLayout = {
  padding: 14,
  gap: 12,
  titleWrap: 'wrap',
  fields: '1fr',
};

/** Rótulo em caixa alta das seções do corpo do drawer. */
function SectionLabel({ children, dark }: Readonly<{ children: ReactNode; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        ...rotuloCaixaAlta,
        fontSize: 11,
        fontWeight: 700,
        color: p.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Par rótulo + valor do rodapé do cartão. `code` = valor literal (mono, na cor
 * do texto principal); sem ele o valor é texto corrido, no cinza do corpo.
 */
function CriterionField({
  label,
  children,
  dark,
  code,
}: Readonly<{ label: string; children: ReactNode; dark: boolean; code?: boolean }>) {
  const p = makePalette(dark);
  return (
    <div>
      <div
        style={{
          ...rotuloCaixaAlta,
          fontSize: 9,
          fontWeight: 700,
          color: p.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 11,
          color: code ? p.text : p.textMuted,
          fontFamily: code ? fontMono : undefined,
          lineHeight: 1.5,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * "1 / de 8" — o peso do critério. É componente porque o cartão o renderiza em
 * DUAS posições: terceira coluna do cabeçalho (tablet e desktop, como sempre) ou
 * dentro da fileira do título (celular). O desenho é o mesmo nos dois lugares.
 */
function ScoreBox({ dark }: Readonly<{ dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        textAlign: 'center',
        flexShrink: 0,
        padding: '6px 10px',
        borderRadius: 6,
        background: p.surface2,
        minWidth: 60,
      }}
    >
      <div
        style={{
          ...numeroTabular,
          fontSize: 18,
          fontWeight: 700,
          color: p.text,
          lineHeight: 1,
          letterSpacing: '-0.02em',
        }}
      >
        1
      </div>
      <div
        style={{
          ...rotuloCaixaAlta,
          fontSize: 8,
          fontWeight: 700,
          color: p.muted,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          marginTop: 2,
        }}
      >
        de {COMPLIANCE_TOTAL}
      </div>
    </div>
  );
}

function CriterionCard({
  id,
  c,
  dark,
  accent,
  index,
}: Readonly<{
  id: ComplianceKey;
  c: AuditCriterion;
  dark: boolean;
  accent: string;
  index: number;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const L = vp.mobile ? CRITERION_NARROW : CRITERION_WIDE;
  // Critério com dispensa cadastrada é o "condicional"; sem dispensa, é exigido
  // de toda aplicação. A regra vive em @/lib/compliance, não repetida aqui.
  const exemption = COMPLIANCE_EXEMPTIONS[id];
  const scopeColor = exemption ? '#ea580c' : '#dc2626';
  return (
    <div
      style={{
        padding: L.padding,
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: L.gap,
      }}
    >
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            ...numeroTabular,
            width: 30,
            height: 30,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
            color: '#fff',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {String(index + 1).padStart(2, '0')}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: L.titleWrap,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 700,
                color: p.text,
                letterSpacing: '-0.01em',
              }}
            >
              {COMPLIANCE_LABELS[id]}
            </div>
            <span
              style={{
                ...rotuloCaixaAlta,
                fontSize: 9,
                fontWeight: 700,
                color: scopeColor,
                padding: '2px 6px',
                borderRadius: 3,
                background: dark ? `${scopeColor}22` : `${scopeColor}18`,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {exemption ? 'com dispensa' : 'sempre exigido'}
            </span>
            {/* O peso do critério entra AQUI no celular, na fileira que já
                embrulha: como terceira coluna do cabeçalho ele consumia 72px dos
                317px do cartão e a descrição ficava lendo em 185px — cinco
                palavras por linha. Fora dele, sobram 275px para a descrição. */}
            {vp.mobile && <ScoreBox dark={dark} />}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: p.textMuted,
              lineHeight: 1.55,
            }}
          >
            {c.description}
          </div>
          {exemption && (
            <div
              style={{
                fontSize: 11,
                color: p.muted,
                lineHeight: 1.5,
                marginTop: 6,
              }}
            >
              <span style={{ ...rotuloCaixaAlta, textTransform: 'uppercase' }}>Dispensa</span> ·{' '}
              {exemption}. Um critério dispensado conta como atendido.
            </div>
          )}
        </div>
        {/* No celular o peso mora na fileira do título (acima); aqui ele só
            aparece a partir do tablet. */}
        {!vp.mobile && <ScoreBox dark={dark} />}
      </div>

      {/* O que a auditoria espera */}
      <div>
        <div
          style={{
            ...rotuloCaixaAlta,
            fontSize: 9,
            fontWeight: 700,
            color: p.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            marginBottom: 8,
          }}
        >
          O que a auditoria espera
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {c.expects.map((v) => (
            <div
              key={v}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: 12,
                color: p.textMuted,
                lineHeight: 1.45,
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  flexShrink: 0,
                  background: dark ? '#16a34a22' : '#dcfce7',
                  color: '#16a34a',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 1,
                }}
              >
                <IconCheck size={11} />
              </span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Evidência + impacto */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: L.fields,
          gap: 12,
          paddingTop: 12,
          borderTop: `1px solid ${p.border}`,
        }}
      >
        <CriterionField label="Evidência aceita" dark={dark} code>
          {c.evidence}
        </CriterionField>
        <CriterionField label="Impacto se ausente" dark={dark}>
          {c.fail}
        </CriterionField>
      </div>
    </div>
  );
}

// Faixas reais: contagem de critérios atendidos, não porcentagem inventada.
function ScoringScale({ dark }: Readonly<{ dark: boolean }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Grade e "faixa larga" saem da MESMA decisão: com uma coluna só, o
  // `gridColumn: span 2` da faixa de falha criaria uma SEGUNDA coluna implícita
  // (com o dobro da largura do drawer) em vez de esticar o cartão.
  const scale = byViewport<{ columns: string; wide: string | undefined }>(vp, {
    mobile: { columns: 'minmax(0, 1fr)', wide: undefined },
    tablet: { columns: 'repeat(2, minmax(0, 1fr))', wide: 'span 2' },
    desktop: { columns: 'repeat(2, 1fr)', wide: 'span 2' },
  });
  const pctOf = (ok: number) => `${((ok / COMPLIANCE_TOTAL) * 100).toFixed(1)}%`;
  const tiers: readonly {
    level: ComplianceLevel;
    range: string;
    sub: string;
    wide?: boolean;
  }[] = [
    {
      level: 'total',
      range: `${COMPLIANCE_TOTAL} / ${COMPLIANCE_TOTAL}`,
      sub: `Todos os critérios atendidos, contando as dispensas por tipo e visibilidade (${pctOf(COMPLIANCE_TOTAL)} de cobertura).`,
    },
    {
      level: 'parcial',
      range: `${COMPLIANCE_PARTIAL_FLOOR} a ${COMPLIANCE_TOTAL - 1} / ${COMPLIANCE_TOTAL}`,
      sub: `De ${COMPLIANCE_PARTIAL_FLOOR} a ${COMPLIANCE_TOTAL - 1} critérios atendidos (${pctOf(COMPLIANCE_PARTIAL_FLOOR)} a ${pctOf(COMPLIANCE_TOTAL - 1)}). As pendências entram no plano de remediação.`,
    },
    {
      level: 'falha',
      range: `0 a ${COMPLIANCE_PARTIAL_FLOOR - 1} / ${COMPLIANCE_TOTAL}`,
      sub: `Menos de ${COMPLIANCE_PARTIAL_FLOOR} critérios atendidos. A aplicação aparece no topo da lista de atenção da auditoria.`,
      wide: true,
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: scale.columns,
        gap: 12,
      }}
    >
      {tiers.map((t) => {
        const color = complianceLevelColor(t.level);
        return (
          <div
            key={t.level}
            style={{
              padding: 14,
              background: p.surface,
              border: `1px solid ${p.border}`,
              borderLeft: `3px solid ${color}`,
              borderRadius: 8,
              gridColumn: t.wide ? scale.wide : undefined,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color,
                }}
              >
                {COMPLIANCE_LEVEL_LABELS[t.level]}
              </div>
              <div
                style={{
                  ...numeroTabular,
                  fontSize: 13,
                  fontWeight: 700,
                  color: p.text,
                }}
              >
                {t.range}
              </div>
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: p.textMuted,
                lineHeight: 1.5,
              }}
            >
              {t.sub}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Drawer · desliza da direita sobre a tela de auditoria
export function ScreenAuditWithDrawer({
  dark,
  accent,
  accentDark,
  apps,
  onRefresh,
  refreshing,
  dataUpdatedAt,
}: Readonly<
  ScreenProps & {
    apps: App[];
    /** Repassado à tela de auditoria ao fundo; sem callback, o botão não aparece. */
    onRefresh?: () => void;
    refreshing?: boolean;
    dataUpdatedAt?: number;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();

  // Números reais do inventário carregado — nenhum é literal.
  const stats = useMemo(() => {
    const results = apps.map((a) => complianceOf(a));
    const complete = results.filter((r) => r.level === 'total').length;
    const avg = results.length ? results.reduce((acc, r) => acc + r.pct, 0) / results.length : 0;
    return { apps: apps.length, complete, avg };
  }, [apps]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        fontFamily: fontSans,
      }}
    >
      {/* Tela de auditoria ao fundo */}
      <ScreenAudit
        dark={dark}
        accent={accent}
        accentDark={accentDark}
        apps={apps}
        onRefresh={onRefresh}
        refreshing={refreshing}
        dataUpdatedAt={dataUpdatedAt}
      />

      {/* Fundo escurecido · clicar fecha o drawer, então é um botão de verdade
          (teclado e leitor de tela funcionam sem handler improvisado). */}
      <button
        type="button"
        aria-label="Fechar critérios da auditoria"
        onClick={() => navigate('/admin/audit')}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(2px)',
          border: 'none',
          padding: 0,
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          ...byViewport(vp, {
            mobile: DRAWER_NARROW,
            tablet: DRAWER_TABLET,
            desktop: DRAWER_WIDE,
          }),
          background: p.bg,
          borderLeft: `1px solid ${p.border}`,
          boxShadow: '-20px 0 60px -20px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Cabeçalho do drawer */}
        <div
          style={{
            padding: byViewport(vp, {
              mobile: '16px 14px',
              tablet: '22px 24px',
              desktop: '22px 24px',
            }),
            borderBottom: `1px solid ${p.border}`,
            background: p.surface,
            display: 'flex',
            alignItems: 'flex-start',
            gap: byViewport(vp, { mobile: 10, tablet: 14, desktop: 14 }),
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconShield size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                ...rotuloCaixaAlta,
                fontSize: 10,
                fontWeight: 700,
                color: accent,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                marginBottom: 4,
              }}
            >
              Auditoria 2026
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: p.text,
                letterSpacing: '-0.018em',
                marginBottom: 4,
              }}
            >
              Critérios de avaliação
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: p.textMuted,
                lineHeight: 1.55,
              }}
            >
              Como cada aplicação é avaliada e quais evidências a auditoria aceita em cada item.
            </div>
          </div>
          <button
            type="button"
            aria-label="Fechar critérios da auditoria"
            onClick={() => navigate('/admin/audit')}
            style={{
              // 40px no toque (o X é o jeito principal de sair do drawer no
              // celular); no desktop segue com os 32px de antes.
              width: byViewport(vp, { mobile: 40, tablet: 32, desktop: 32 }),
              height: byViewport(vp, { mobile: 40, tablet: 32, desktop: 32 }),
              borderRadius: 8,
              background: 'transparent',
              color: p.muted,
              border: `1px solid ${p.border}`,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconX size={15} />
          </button>
        </div>

        {/* Corpo · rolável */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: byViewport(vp, { mobile: 14, tablet: 20, desktop: 24 }),
            display: 'flex',
            flexDirection: 'column',
            gap: byViewport(vp, { mobile: 14, tablet: 18, desktop: 18 }),
          }}
        >
          {/* Resumo de como funciona */}
          <div
            style={{
              padding: byViewport(vp, { mobile: 14, tablet: 18, desktop: 18 }),
              background: dark ? `${accent}11` : `${accent}08`,
              border: `1px solid ${accent}33`,
              borderRadius: 10,
            }}
          >
            <div
              style={{
                ...rotuloCaixaAlta,
                fontSize: 10,
                fontWeight: 700,
                color: accent,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                marginBottom: 8,
              }}
            >
              Como funciona
            </div>
            <div
              style={{
                fontSize: 13,
                color: p.text,
                lineHeight: 1.65,
              }}
            >
              Cada aplicação é avaliada em <strong>{COMPLIANCE_TOTAL} critérios</strong> de
              cadastro, e cada critério vale <strong>1 ponto</strong>. A cobertura é a razão entre
              atendidos e {COMPLIANCE_TOTAL} (por exemplo, 6 de {COMPLIANCE_TOTAL} = 75%). Parte dos
              critérios tem <strong>dispensa</strong> conforme o tipo e a visibilidade da aplicação,
              e um critério dispensado conta como atendido. O Master Apps confere apenas se o campo
              ou o link está preenchido: a conferência do conteúdo é manual, feita por quem audita.
            </div>
            {/* Os quatro números somam 420px de rótulo + 60px de vãos: em 303px
                úteis (drawer de 375px) só cabem embrulhando em duas fileiras. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: byViewport(vp, { mobile: '10px 18px', tablet: '20px', desktop: '20px' }),
                flexWrap: byViewport(vp, { mobile: 'wrap', tablet: 'nowrap', desktop: 'nowrap' }),
                marginTop: 14,
                paddingTop: 14,
                borderTop: `1px solid ${accent}22`,
              }}
            >
              {[
                { l: 'Critérios', v: String(COMPLIANCE_TOTAL) },
                { l: 'Apps avaliadas', v: String(stats.apps) },
                { l: 'Conformidade total', v: String(stats.complete) },
                { l: 'Cobertura média', v: `${stats.avg.toFixed(0)}%` },
              ].map((s) => (
                <div key={s.l}>
                  <div
                    style={{
                      ...rotuloCaixaAlta,
                      fontSize: 9,
                      fontWeight: 700,
                      color: p.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      marginBottom: 3,
                    }}
                  >
                    {s.l}
                  </div>
                  <div
                    style={{
                      ...numeroTabular,
                      fontSize: 16,
                      fontWeight: 700,
                      color: p.text,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Escala de classificação */}
          <div>
            <SectionLabel dark={dark}>Faixas de classificação</SectionLabel>
            <ScoringScale dark={dark} />
          </div>

          {/* Lista de critérios · na ordem canônica de @/lib/compliance */}
          <div>
            <SectionLabel dark={dark}>Os {COMPLIANCE_TOTAL} critérios avaliados</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {COMPLIANCE_ORDER.map((key, i: number) => (
                <CriterionCard
                  key={key}
                  id={key}
                  c={AUDIT_CRITERIA[key]}
                  index={i}
                  dark={dark}
                  accent={accent}
                />
              ))}
            </div>
          </div>

          {/* Nota de rodapé */}
          <div
            style={{
              padding: 14,
              background: p.surface,
              border: `1px solid ${p.border}`,
              borderRadius: 8,
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              fontSize: 11.5,
              color: p.textMuted,
              lineHeight: 1.55,
            }}
          >
            <span style={{ color: accent, marginTop: 1 }}>
              <IconLock size={14} />
            </span>
            <div>
              <strong style={{ color: p.text }}>De onde vêm os números.</strong> Não existe coleta
              automática nem snapshot diário: os percentuais são calculados no navegador, a partir
              do cadastro do inventário, no momento em que a tela é aberta. Os artefatos para a
              auditoria externa são gerados sob demanda em CSV, pelo botão "Exportar CSV" da tela de
              auditoria.
            </div>
          </div>
        </div>

        {/* Rodapé do drawer · ações. No celular a frase e o botão empilham: lado
            a lado, a frase de 340px quebrava em três linhas e comprimia o botão. */}
        <div
          style={{
            padding: byViewport(vp, {
              mobile: '12px 14px',
              tablet: '14px 24px',
              desktop: '14px 24px',
            }),
            borderTop: `1px solid ${p.border}`,
            background: p.surface,
            display: 'flex',
            flexDirection: byViewport(vp, { mobile: 'column', tablet: 'row', desktop: 'row' }),
            alignItems: byViewport(vp, { mobile: 'stretch', tablet: 'center', desktop: 'center' }),
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: p.muted,
              fontFamily: fontSans,
            }}
          >
            Mesma regra aplicada na auditoria e no acervo de documentos
          </span>
          <button
            type="button"
            onClick={() => navigate('/admin/audit')}
            style={{
              // 40px de alvo no toque; 32px, como antes, a partir do tablet.
              height: byViewport(vp, { mobile: 40, tablet: 32, desktop: 32 }),
              padding: '0 14px',
              background: accent,
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
